import styled from 'styled-components';

interface IconProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  color?: string;
  rotation?: number;
  clickable?: boolean;
  disabled?: boolean;
  margin?: string;
  padding?: string;
}

const getIconSize = (size: string | number) => {
  if (typeof size === 'number') {
    return `${size}px`;
  }
  
  switch (size) {
    case 'xs': return '12px';
    case 'sm': return '16px';
    case 'md': return '20px';
    case 'lg': return '24px';
    case 'xl': return '32px';
    default: return '20px';
  }
};

export const Icon = styled.i<IconProps>`
  font-size: ${props => getIconSize(props.size || 'md')};
  color: ${props => props.color || 'currentColor'};
  transform: ${props => props.rotation ? `rotate(${props.rotation}deg)` : 'none'};
  cursor: ${props => {
    if (props.disabled) return 'not-allowed';
    if (props.clickable) return 'pointer';
    return 'inherit';
  }};
  opacity: ${props => props.disabled ? '0.5' : '1'};
  margin: ${props => props.margin || '0'};
  padding: ${props => props.padding || '0'};
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  
  ${props => props.clickable && !props.disabled && `
    &:hover {
      opacity: 0.8;
      transform: ${props.rotation ? `rotate(${props.rotation}deg) scale(1.1)` : 'scale(1.1)'};
    }
  `}
`;

export const IconButton = styled.button<IconProps>`
  background: none;
  border: none;
  padding: ${props => props.padding || '0.5rem'};
  margin: ${props => props.margin || '0'};
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? '0.5' : '1'};
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.05);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  }
  
  ${Icon} {
    font-size: ${props => getIconSize(props.size || 'md')};
    color: ${props => props.color || 'currentColor'};
  }
`;

// Specialized icon variants
export const SearchIcon = styled(Icon)`
  color: #6b7280;
`;

export const CloseIcon = styled(Icon)`
  color: #6b7280;
  cursor: pointer;
  
  &:hover {
    color: #374151;
  }
`;

export const EditIcon = styled(Icon)`
  color: #667eea;
  cursor: pointer;
  
  &:hover {
    color: #5a67d8;
  }
`;

export const DeleteIcon = styled(Icon)`
  color: #ef4444;
  cursor: pointer;
  
  &:hover {
    color: #dc2626;
  }
`;

export const InfoIcon = styled(Icon)`
  color: #3b82f6;
`;

export const WarningIcon = styled(Icon)`
  color: #f59e0b;
`;

export const ErrorIcon = styled(Icon)`
  color: #ef4444;
`;

export const SuccessIcon = styled(Icon)`
  color: #10b981;
`;

export const LoadingIcon = styled(Icon)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// Icon containers
export const IconContainer = styled.div<{ background?: string; borderRadius?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.background || 'transparent'};
  border-radius: ${props => props.borderRadius || '50%'};
  padding: 0.5rem;
`;

export const IconWithText = styled.div<{ gap?: string; align?: string }>`
  display: flex;
  align-items: ${props => props.align || 'center'};
  gap: ${props => props.gap || '0.5rem'};
`;